import {
  ApiClient,
  ApiMedia,
  ApiMediaPayload,
  ApiMediaType,
  ApiProject,
} from "@incendium/api";
import { mediaService } from "Apis";
import produce from "immer";
import { CallbackOrVal } from "Interfaces";
import { S3Response } from "features/media/types";

function base64DataUrlToBlob(dataUrl: string): { blob: Blob; mime: string } {
  const [header, base64] = dataUrl.split(",");
  const mimeMatch = header.match(/data:([^;]+);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return { blob: new Blob([bytes], { type: mime }), mime };
}

export async function saveBase64ImageToMedia(
  selectedClient: ApiClient,
  selectedProject: ApiProject,
  setMedias: (val: CallbackOrVal<ApiMedia[]>) => void,
  base64DataUrl: string,
): Promise<string> {
  const { blob, mime } = base64DataUrlToBlob(base64DataUrl);
  const filename = `gen-${Date.now()}.png`;
  const path = `lander/gen`;
  const { url: presignedURL } = await mediaService.mediaServiceGetPresignedURL({
    projectId: selectedProject?.id as number,
    payload: { name: filename, path, type: ApiMediaType.IMAGE_MEDIA },
  });
  if (!presignedURL) {
    throw new Error("Failed to get presigned URL");
  }
  const response = await fetch(presignedURL, {
    method: "PUT",
    body: blob,
    headers: {
      "Content-Type": mime,
      "Access-Control-Allow-Origin": "*",
    },
  });
  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
  const publicUrl = presignedURL.split("?").shift() || "";
  const payload: ApiMediaPayload = {
    name: filename,
    type: ApiMediaType.IMAGE_MEDIA,
    files: [
      {
        fullPath: publicUrl,
        fileType: mime,
      },
    ],
  };
  const res = await mediaService.mediaServiceCreateMedia({
    projectId: selectedProject?.id as number,
    payload,
  });
  setMedias((prevMedias) =>
    produce(prevMedias as ApiMedia[], (draft) => {
      draft.push(res);
    }),
  );
  return (res.files || [])[0].fullPath || "";
}

export const customAssetManager = (
  selectedClient: ApiClient,
  selectedProject: ApiProject,
  assets: string[],
  setMedias: (val: CallbackOrVal<ApiMedia[]>) => void,
) => {
  const onGetSignedUrl = async (file: File, path: string) => {
    const { url } = await mediaService.mediaServiceGetPresignedURL({
      projectId: selectedProject?.id as number,
      payload: {
        name: file.name,
        path,
        type: ApiMediaType.IMAGE_MEDIA,
      },
    });
    return url;
  };

  const uploadSingleFile = async (file: File): Promise<string> => {
    const path = `lander/media`;
    const presignedURL = await onGetSignedUrl(file, path);

    if (!presignedURL) {
      throw new Error("Failed to get presigned URL");
    }

    // Upload file to S3 using fetch
    const response = await fetch(presignedURL, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
        "Access-Control-Allow-Origin": "*",
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    // Create S3Response object
    const result: S3Response = {
      filename: file.name,
      fileKey: "", // This would be set by your backend if needed
      signedUrl: presignedURL,
      publicUrl: presignedURL.split("?").shift() || "",
    };

    const payload: ApiMediaPayload = {
      name: result.filename,
      type: ApiMediaType.IMAGE_MEDIA,
      files: [
        {
          fullPath: result.publicUrl,
          fileType: file.type,
        },
      ],
    };

    const res = await mediaService.mediaServiceCreateMedia({
      projectId: selectedProject?.id as number,
      payload,
    });

    setMedias((prevMedias) =>
      produce(prevMedias as ApiMedia[], (draft) => {
        draft.push(res);
      }),
    );

    return (res.files || [])[0].fullPath || "";
  };

  return {
    upload: `clients/${selectedClient?.id}/projects/${selectedProject?.id}`,
    multiUpload: true,
    assets,
    uploadFile: async (ev: DragEvent) => {
      ev.preventDefault();

      if (!ev.dataTransfer?.files && !(ev.target as any).files) {
        return;
      }

      const files = Array.from(
        ev.dataTransfer?.files || ((ev.target as any).files as FileList) || [],
      );
      const imageFiles = files.filter((file) => file.type.startsWith("image/"));

      if (imageFiles.length === 0) {
        console.warn("No image files found in drop");
        return;
      }

      // Emit asset:upload:start event
      const assetManager = (ev.target as any).__gjsAssetManager;
      if (assetManager) {
        assetManager.trigger("asset:upload:start", { files: imageFiles });
      }

      try {
        const uploadPromises = imageFiles.map(async (file) => {
          try {
            const url = await uploadSingleFile(file);

            // Emit asset:upload:success event for each file
            if (assetManager) {
              assetManager.trigger("asset:upload:success", {
                file,
                url,
                name: file.name,
                type: file.type,
              });
            }

            return { file, url, success: true };
          } catch (error) {
            console.error("Upload failed for file:", file.name, error);

            // Emit asset:upload:error event for failed files
            if (assetManager) {
              assetManager.trigger("asset:upload:error", {
                file,
                error: error instanceof Error ? error.message : "Upload failed",
              });
            }

            return {
              file,
              error: error instanceof Error ? error.message : "Upload failed",
              success: false,
            };
          }
        });

        const results = await Promise.all(uploadPromises);

        // Emit asset:upload:end event
        if (assetManager) {
          assetManager.trigger("asset:upload:end", { results });
        }

        // Add successful uploads to asset manager
        const successfulUploads = results.filter((r) => r.success);
        successfulUploads.forEach((result) => {
          if (assetManager && result.url) {
            assetManager.add(result.url);
          }
        });
      } catch (error) {
        console.error("Upload process failed:", error);

        // Emit asset:upload:error event for overall failure
        if (assetManager) {
          assetManager.trigger("asset:upload:error", {
            error:
              error instanceof Error ? error.message : "Upload process failed",
          });
        }
      }
    },
  };
};
